import React, { useEffect, useMemo, useState } from 'react';
import client, { API_URL } from '../api';
import './ProjectPromotionStudio.css';

const COPY = {
  he: {
    eyebrow: 'PROMOTE', title: 'סטודיו פרסום',
    subtitle: 'חבילת קמפיין דטרמיניסטית לאתר הזה: עמוד נחיתה קטן, כרטיס שיתוף, קישורי UTM וכניסה ישירה למנהלי הפרסום — בלי להפעיל את הסוכן.',
    loading: 'מכין את חבילת הפרסום…', unavailable: 'כדי לפרסם צריך קודם כתובת Live מאומתת. ה־Preview נשאר ללא שינוי.',
    campaignTitle: 'כותרת הקמפיין', description: 'תיאור קצר', cta: 'טקסט כפתור', campaignName: 'שם קמפיין למעקב', save: 'שמור חבילת קמפיין', saving: 'שומר…', saved: 'חבילת הקמפיין עודכנה.',
    landing: 'עמוד הקמפיין', copy: 'העתק קישור', copied: 'הועתק', openLanding: 'פתח עמוד', channels: 'ערוצי פרסום', channelsHint: 'המערכת מכינה לכל ערוץ קישור מעקב מתאים. פתיחת מנהל הפרסום אינה מפעילה תקציב ואינה מפרסמת בלי אישור שלך.',
    openManager: 'פתח מנהל פרסום', copyTracked: 'העתק קישור לקמפיין', format: 'פורמט', ratio: 'יחס מומלץ', error: 'לא ניתן לטעון את סטודיו הפרסום כרגע.'
  },
  en: {
    eyebrow: 'PROMOTE', title: 'Campaign Studio',
    subtitle: 'A deterministic campaign kit for this site: a focused landing page, share card, UTM links, and direct access to major ad managers — without invoking the agent.',
    loading: 'Preparing campaign kit…', unavailable: 'A verified live URL is required before advertising. Preview remains untouched.',
    campaignTitle: 'Campaign headline', description: 'Short description', cta: 'CTA label', campaignName: 'Tracking campaign name', save: 'Save campaign kit', saving: 'Saving…', saved: 'Campaign kit updated.',
    landing: 'Campaign landing page', copy: 'Copy link', copied: 'Copied', openLanding: 'Open page', channels: 'Advertising channels', channelsHint: 'Each channel gets its own tracked URL. Opening an ads manager never spends money or launches a campaign without your approval.',
    openManager: 'Open ads manager', copyTracked: 'Copy campaign link', format: 'Format', ratio: 'Recommended ratio', error: 'Campaign Studio is unavailable right now.'
  }
};

function language() { return /^he\b/i.test(document.documentElement.lang || navigator.language || '') ? 'he' : 'en'; }
function absolute(url) { return /^https?:\/\//i.test(url || '') ? url : `${API_URL.replace(/\/$/, '')}${url || ''}`; }

export default function ProjectPromotionStudio({ project }) {
  const copy = COPY[language()];
  const [kit, setKit] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState('');
  const projectId = project?.id;

  const canPromote = useMemo(() => Boolean(projectId && ['WEB', 'FULL_STACK'].includes(project?.target || 'WEB')), [projectId, project?.target]);

  const load = async () => {
    if (!projectId || !canPromote) return;
    setLoading(true); setError(''); setMessage('');
    try {
      const { data } = await client.get(`/api/publishing/projects/${encodeURIComponent(projectId)}/campaign`);
      setKit(data);
      setDraft(data.profile ? {
        title: data.profile.title || '', description: data.profile.description || '', cta: data.profile.cta || '', campaignName: data.profile.campaignName || ''
      } : null);
    } catch (err) { setError(err.response?.data?.error || copy.error); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId, canPromote]);

  const save = async () => {
    if (!projectId || !draft) return;
    setSaving(true); setError(''); setMessage('');
    try {
      const { data } = await client.put(`/api/publishing/projects/${encodeURIComponent(projectId)}/campaign`, draft);
      setKit(data); setDraft(data.profile); setMessage(copy.saved);
    } catch (err) { setError(err.response?.data?.error || copy.error); }
    finally { setSaving(false); }
  };

  const copyUrl = async (key, value) => {
    try {
      await navigator.clipboard.writeText(absolute(value));
      setCopied(key);
      window.setTimeout(() => setCopied(current => current === key ? '' : current), 1600);
    } catch (_) { setError(copy.error); }
  };

  if (!canPromote) return null;

  return <section className="project-promotion-studio" aria-labelledby="promotion-studio-title">
    <div className="promotion-heading">
      <div><div className="promotion-eyebrow">{copy.eyebrow}</div><h3 id="promotion-studio-title">{copy.title}</h3><p>{copy.subtitle}</p></div>
      <button type="button" className="promotion-refresh" onClick={load} disabled={loading || saving} aria-label="Refresh campaign studio">↻</button>
    </div>

    {error && <div className="promotion-message error" role="alert">{error}</div>}
    {message && <div className="promotion-message" role="status">{message}</div>}
    {loading && <div className="promotion-empty">{copy.loading}</div>}

    {!loading && kit && !kit.ready && <div className="promotion-notice"><span className="promotion-lock" aria-hidden="true">◈</span><div><strong>{copy.unavailable}</strong><small>{project?.urls?.preview ? 'Preview ready · Live URL still required for durable advertising.' : 'Build and verify the project first, then deploy it when approved.'}</small></div></div>}

    {!loading && kit?.ready && draft && <>
      <div className="promotion-kit-grid">
        <div className="promotion-creative-card">
          <div className="promotion-preview"><img src={absolute(kit.profile.cardUrl)} alt="Campaign creative preview" /></div>
          <div className="promotion-landing-row"><div><span>{copy.landing}</span><code>{absolute(kit.profile.landingUrl)}</code></div><div className="promotion-inline-actions"><button type="button" onClick={() => copyUrl('landing', kit.profile.landingUrl)}>{copied === 'landing' ? copy.copied : copy.copy}</button><a href={absolute(kit.profile.landingUrl)} target="_blank" rel="noreferrer">{copy.openLanding}</a></div></div>
        </div>

        <div className="promotion-editor">
          <label>{copy.campaignTitle}<input value={draft.title || ''} maxLength="90" onChange={event => setDraft(current => ({ ...current, title: event.target.value }))} /></label>
          <label>{copy.description}<textarea rows="4" value={draft.description || ''} maxLength="280" onChange={event => setDraft(current => ({ ...current, description: event.target.value }))} /></label>
          <div className="promotion-editor-row"><label>{copy.cta}<input value={draft.cta || ''} maxLength="40" onChange={event => setDraft(current => ({ ...current, cta: event.target.value }))} /></label><label>{copy.campaignName}<input value={draft.campaignName || ''} maxLength="80" onChange={event => setDraft(current => ({ ...current, campaignName: event.target.value }))} /></label></div>
          <button type="button" className="promotion-save" onClick={save} disabled={saving}>{saving ? copy.saving : copy.save}</button>
        </div>
      </div>

      <div className="promotion-channel-heading"><div><h4>{copy.channels}</h4><p>{copy.channelsHint}</p></div></div>
      <div className="promotion-channel-grid">
        {(kit.channels || []).map(channel => <article className="promotion-channel-card" key={channel.id}>
          <div className={`promotion-channel-mark channel-${channel.id}`} aria-hidden="true">{channel.name.split(' ').map(part => part[0]).join('').slice(0, 2)}</div>
          <div className="promotion-channel-copy"><strong>{channel.name}</strong><span>{copy.format}: {channel.format}</span><span>{copy.ratio}: {channel.ratio}</span></div>
          <div className="promotion-channel-actions"><button type="button" onClick={() => copyUrl(channel.id, channel.landingUrl)}>{copied === channel.id ? copy.copied : copy.copyTracked}</button><a href={channel.launchUrl} target="_blank" rel="noopener noreferrer">{copy.openManager} ↗</a></div>
        </article>)}
      </div>
    </>}
  </section>;
}
